import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../components/Button";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { apiRequest, assetUrl } from "../../api/client";
import { useToast } from "../../context/ToastContext";

const emptyProduct = {
  name: "",
  slug: "",
  description: "",
  short_description: "",
  price: "",
  discount_price: "",
  category: "Nutrition eBook",
  image_url: "",
  image_public_id: "",
  gallery_images: [],
  ebook_file_path: "",
  ebook_public_id: "",
  download_link: "",
  status: "active",
  featured: false,
  stock_type: "unlimited",
  stock_quantity: "",
  product_type: "digital",
};

const imageTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const maxImageSize = 10 * 1024 * 1024;
const maxPdfSize = 25 * 1024 * 1024;

function fileLabel(publicId, fallback) {
  if (!publicId) return fallback;
  return publicId.split("/").pop() || fallback;
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState(emptyProduct);
  const [imageFile, setImageFile] = useState(null);
  const [ebookFile, setEbookFile] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(form.image_url ? assetUrl(form.image_url) : "");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(imageFile);
    setImagePreview(previewUrl);
    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [form.image_url, imageFile]);

  useEffect(() => {
    if (!id) return;
    apiRequest("/api/products?admin=true", { tokenType: "admin" })
      .then((response) => {
        const product = response.data.find((item) => String(item.id) === String(id));
        if (!product) throw new Error("Product not found.");
        setForm({
          ...emptyProduct,
          ...product,
          discount_price: product.discount_price ?? "",
          stock_quantity: product.stock_quantity ?? "",
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectImage(event) {
    const file = event.target.files?.[0] || null;
    setError("");
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!imageTypes.has(file.type)) {
      event.target.value = "";
      setError("Choose a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > maxImageSize) {
      event.target.value = "";
      setError("Product images must be 10 MB or smaller.");
      return;
    }
    setImageFile(file);
  }

  function selectEbook(event) {
    const file = event.target.files?.[0] || null;
    setError("");
    if (!file) {
      setEbookFile(null);
      return;
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      event.target.value = "";
      setError("Choose a PDF file for the e-book.");
      return;
    }
    if (file.size > maxPdfSize) {
      event.target.value = "";
      setError("E-book PDFs must be 25 MB or smaller.");
      return;
    }
    setEbookFile(file);
  }

  function productFormData() {
    const data = new FormData();
    [
      "name",
      "slug",
      "description",
      "short_description",
      "price",
      "discount_price",
      "category",
      "download_link",
      "status",
      "stock_type",
      "stock_quantity",
      "product_type",
    ].forEach((field) => data.append(field, form[field] ?? ""));
    data.append("featured", String(form.featured));
    data.append("gallery_images", JSON.stringify(form.gallery_images || []));
    if (imageFile) data.append("image", imageFile);
    if (ebookFile) data.append("ebook", ebookFile);
    return data;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!id && !imageFile) {
      setError("Choose a product image before saving.");
      return;
    }
    if (!id && form.product_type === "digital" && !ebookFile && !form.download_link) {
      setError("Choose an e-book PDF or provide an external download link.");
      return;
    }

    setSaving(true);
    setUploadProgress(0);
    setError("");
    try {
      const method = id ? "PUT" : "POST";
      const endpoint = id ? `/api/products/${id}` : "/api/products";
      const response = await apiRequest(endpoint, {
        method,
        body: productFormData(),
        tokenType: "admin",
        onUploadProgress: setUploadProgress,
      });
      const warning = response.warnings?.[0];
      toast?.show(warning || "Product saved.");
      navigate("/admin/products");
    } catch (err) {
      setError(err.details ? `${err.message} ${err.details}` : err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div><span className="eyebrow">Catalog</span><h1>{id ? "Edit Product" : "Add Product"}</h1></div>
      </div>
      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="admin-panel form-card">
          <div className="form-grid">
            <label><span>Name</span><input required value={form.name} onChange={(event) => update("name", event.target.value)} /></label>
            <label><span>Slug</span><input value={form.slug} onChange={(event) => update("slug", event.target.value)} placeholder="auto from name" /></label>
          </div>
          <label><span>Short description</span><input value={form.short_description} onChange={(event) => update("short_description", event.target.value)} /></label>
          <label><span>Description</span><textarea required rows="6" value={form.description} onChange={(event) => update("description", event.target.value)} /></label>
          <div className="form-grid">
            <label><span>Price</span><input required min="0" step="0.01" type="number" value={form.price} onChange={(event) => update("price", event.target.value)} /></label>
            <label><span>Discount price</span><input min="0" step="0.01" type="number" value={form.discount_price} onChange={(event) => update("discount_price", event.target.value)} /></label>
          </div>
          <div className="form-grid">
            <label><span>Category</span><input value={form.category} onChange={(event) => update("category", event.target.value)} /></label>
            <label><span>Status</span><select value={form.status} onChange={(event) => update("status", event.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
          </div>

          <div className="product-upload-grid">
            <label className="product-upload-card">
              <span>Product image</span>
              <input disabled={saving} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={selectImage} />
              <small>{imageFile ? `${imageFile.name} selected` : id ? "Choose a file to replace the current image." : "JPEG, PNG, WebP, or GIF. Maximum 10 MB."}</small>
              {imagePreview && <img className="product-image-preview" src={imagePreview} alt="Product preview" />}
            </label>

            <label className="product-upload-card">
              <span>E-book PDF</span>
              <input disabled={saving} type="file" accept="application/pdf,.pdf" onChange={selectEbook} />
              <small>{ebookFile ? `${ebookFile.name} selected` : id ? "Choose a PDF to replace the current e-book." : "PDF only. Maximum 25 MB."}</small>
              {form.ebook_file_path && !ebookFile && (
                <div className="current-file">
                  <strong>Current PDF</strong>
                  <span>{fileLabel(form.ebook_public_id, "Secure e-book PDF")}</span>
                </div>
              )}
            </label>
          </div>

          <label><span>External download link</span><input type="url" value={form.download_link || ""} onChange={(event) => update("download_link", event.target.value)} /></label>
          <div className="admin-checks">
            <label><input type="checkbox" checked={form.featured} onChange={(event) => update("featured", event.target.checked)} /> Featured product</label>
            <label><input type="checkbox" checked={form.stock_type === "unlimited"} onChange={(event) => update("stock_type", event.target.checked ? "unlimited" : "limited")} /> Unlimited digital stock</label>
          </div>

          {saving && (
            <div className="upload-progress" aria-live="polite">
              <div><span style={{ width: `${uploadProgress}%` }} /></div>
              <strong>{uploadProgress < 100 ? `Uploading files ${uploadProgress}%` : "Processing files and saving product..."}</strong>
            </div>
          )}
          {error && <p className="form-status error">{error}</p>}
          <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save Product"}</Button>
        </div>
      </form>
    </div>
  );
}
